import { useState, useEffect } from 'react'
import { useTranslation } from '@/i18n/hooks/useTranslation'
import { useSimpleNotifications } from '@/features/notifications/SimpleNotificationProvider'

export function SimpleNotificationBanner() {
  const { t } = useTranslation()
  const { isSupported, permission, requestPermission } = useSimpleNotifications()
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    const checkShouldShowBanner = () => {
      if (!isSupported) {
        setShowBanner(false)
        return
      }

      const hasAskedBefore = localStorage.getItem('notification-permission-asked') === 'true'
      const notificationsEnabled = localStorage.getItem('notifications-enabled') !== 'false'
      
      // Show banner if:
      // 1. Notifications are supported
      // 2. Permission is 'default' (never asked)
      // 3. User hasn't been asked before OR notifications are enabled in app settings
      const shouldShow = permission === 'default' && 
                        (!hasAskedBefore || notificationsEnabled)

      setShowBanner(shouldShow)
    }

    checkShouldShowBanner()

    // Check every 5 seconds
    const interval = setInterval(checkShouldShowBanner, 5000)

    // Listen for storage changes
    const handleStorageChange = () => {
      checkShouldShowBanner()
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [isSupported, permission])

  const handleEnableNotifications = async () => {
    try {
      const newPermission = await requestPermission()
      
      if (newPermission === 'granted') {
        setShowBanner(false)
        if (import.meta.env.DEV) {
          console.log('✅ Notification permission granted')
        }
      } else {
        if (import.meta.env.DEV) {
          console.log('❌ Notification permission denied')
        }
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem('notification-permission-asked', 'true')
    localStorage.setItem('notifications-enabled', 'false')
    setShowBanner(false)
  }

  if (!showBanner) {
    return null
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 bg-blue-600 text-white rounded-xl shadow-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-sm mb-1">
            {t('notifications.enableTitle', 'Enable Notifications')}
          </h3>
          <p className="text-xs opacity-90">
            {t('notifications.enableDescription', 'Get reminded about your challenges to stay on track!')}
          </p>
        </div>
        <div className="flex gap-2 ml-4">
          <button
            onClick={handleDismiss}
            className="text-xs px-3 py-1 rounded border border-white/20 hover:bg-white/10 transition-colors"
          >
            {t('common.dismiss', 'Dismiss')}
          </button>
          <button
            onClick={handleEnableNotifications}
            className="text-xs px-3 py-1 rounded bg-white text-blue-600 hover:bg-gray-100 transition-colors font-medium"
          >
            {t('notifications.enable', 'Enable')}
          </button>
        </div>
      </div>
    </div>
  )
} 